import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Grid,
  Link,
  Popover,
  TextField,
  Typography,
} from "@material-ui/core";
import { differenceInDays, format, formatISO, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import PredictedTrends from "../../components/dashboard/charts/PredictedTrends";
import PredictedTrendsWithCheckboxes from "../../components/dashboard/charts/PredictedTrendsWithCheckboxes";
import PredictedTrendsWithSelects from "../../components/dashboard/charts/PredictedTrendsWithSelects";
import ChevronDownIcon from "../../icons/ChevronDown";
import ChevronRightIcon from "../../icons/ChevronRight";
import {
  GET_DESTINATION_BY_COUNTRY,
  GET_REGIONS_AND_CITIES,
} from "../../queries/prediction/destinationByCountryQuery";
import {
  GET_TRIP_ANALYTICS,
  GET_TRIP_ANALYTICS_META,
  GET_TYPE_ENUMS,
} from "../../queries/prediction/tripAnalyticsQuery";
import { graphQLApiService } from "../../services/GraphQLApiService";
import { hasPermission } from "../../utils/handlePermissions";
import { useTranslation } from "react-i18next";
import { pl } from "date-fns/locale";

const DestinationByCountry = () => {
  const { t, i18n } = useTranslation();
  const date = new Date();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const [mealTypes, setMealTypes] = useState([]);
  const [offerTypes, setOfferTypes] = useState([]);
  const [transportTypes, setTransportTypes] = useState([]);
  const [dates, setDates] = useState([]);
  const [cities, setCities] = useState([]);

  const [stats, setStats] = useState({ min: [], max: [], avg: [] });
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({ value: "GR" });
  const [selectedOfferType, setSelectedOfferType] = useState("STAY");
  const [selectedTransport, setSelectedTransport] = useState("FLIGHT");
  const [selectedDepartureCity, setSelectedDepartureCity] = useState("Gdansk");
  const [selectedPeriodLength, setSelectedPeriodLength] = useState(7);
  const [selectedMealCode, setSelectedMealCode] = useState("AI");

  const [departureCityNames, setDepartureCityNames] = useState([]);
  const [departureAirportCodeNames, setDepartureAirportCodeNames] = useState(
    []
  );
  const [departureStats, setDepartureStats] = useState({
    min: [],
    max: [],
    avg: [],
  });

  const [mealNames, setMealNames] = useState([]);
  const [mealStats, setMealStats] = useState({
    min: [],
    max: [],
    avg: [],
  });
  const [propertyCategoryNames, setPropertyCategoryNames] = useState([]);
  const [propertyCategoryStats, setPropertyCategoryStats] = useState({
    min: [],
    max: [],
    avg: [],
  });
  const [transportTypeNames, setTransportTypeNames] = useState([]);
  const [transportTypeStats, setTransportTypeStats] = useState({
    min: [],
    max: [],
    avg: [],
  });
  const [tripDurationNames, setTripDurationNames] = useState([]);
  const [tripDurationStats, setTripDurationStats] = useState({
    min: [],
    max: [],
    avg: [],
  });

  const [selectionRange, setState] = useState([
    {
      startDate: subDays(new Date("2023-07-31"), 30),
      endDate: new Date("2023-07-31"),
      key: "selection",
    },
  ]);

  useEffect(() => {
    graphQLApiService
      .query(GET_TRIP_ANALYTICS_META, {
        startDate: formatISO(selectionRange[0].startDate).split("T")[0],
        endDate: formatISO(selectionRange[0].endDate).split("T")[0],
      })
      .then((result: any) => {
        const countries = result?.data?.tripAnalytics?.countries?.nodes || [];
        setCountries(
          countries.map((item) => ({
            value: item.countryCode,
            label: item.name,
          }))
        );
      });

    graphQLApiService
      .query(GET_TRIP_ANALYTICS, {
        startDate: format(selectionRange[0].startDate, "yyyy-MM-dd"),
        endDate: format(selectionRange[0].endDate, "yyyy-MM-dd"),
      })
      .then((data) => {
        let stats = { min: [], max: [], avg: [] };
        let names = [];
        let airportCodes = [];

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByDepartureFrom.forEach((item) => {
          names.push(item.departureCity);
          airportCodes.push(item.departureAirportCode);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setDepartureStats(stats);
        setDepartureCityNames(names);
        setDepartureAirportCodeNames(airportCodes);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByMeal.forEach((item) => {
          names.push(item.mealCode);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setMealStats(stats);
        setMealNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByPropertyCategory.forEach((item) => {
          names.push(item.propertyCategory);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setPropertyCategoryStats(stats);
        setPropertyCategoryNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByTransportType.forEach((item) => {
          names.push(item.transportType);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setTransportTypeStats(stats);
        setTransportTypeNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByTripDuration.forEach((item) => {
          names.push(item.tripDuration);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setTripDurationStats(stats);
        setTripDurationNames(names);
      });

    graphQLApiService.query(GET_TYPE_ENUMS, {}).then((result: any) => {
      setMealTypes(
        (result?.data?.MealCode?.enumValues || []).map((item) => ({
          label: item.description,
          value: item.name,
        }))
      );
      setOfferTypes(
        (result?.data?.Offer?.enumValues || []).map((item) => ({
          label: item.description,
          value: item.name,
        }))
      );
      setTransportTypes(
        (result?.data?.Transport?.enumValues || []).map((item) => ({
          label: item.description,
          value: item.name,
        }))
      );
    });

    graphQLApiService
      .query(GET_REGIONS_AND_CITIES, {
        startDate: formatISO(selectionRange[0].startDate).split("T")[0],
        endDate: formatISO(selectionRange[0].endDate).split("T")[0],
        countryCode: selectedCountry.value,
      })
      .then((result: any) => {
        if (result.data) {
          let cities = [];
          result.data.tripAnalytics?.country?.region?.cities?.nodes.forEach(
            (item) => {
              cities.push(item.name);
            }
          );
          setCities(cities);
        } else {
          setCities([]);
        }
      });
  }, [selectionRange, selectedCountry]);

  useEffect(() => {
    graphQLApiService
      .query(GET_DESTINATION_BY_COUNTRY, {
        startDate: formatISO(selectionRange[0].startDate).split("T")[0],
        endDate: formatISO(selectionRange[0].endDate).split("T")[0],
        countryCode: [selectedCountry.value],
        transportType: selectedTransport,
        offerType: selectedOfferType,
        departureCity: selectedDepartureCity,
        length: selectedPeriodLength || 7,
        mealCode: selectedMealCode,
        limit: 10000,
      })
      .then((result: any) => {
        const data = result?.data?.tripAnalytics?.statsByDate || null;
        if (data && data.length > 0) {
          let dates = [];
          let stat = { min: [], max: [], avg: [] };
          data.forEach((item) => {
            dates.push(item.date);
            stat.min.push(item.minimumBasePrice);
            stat.max.push(item.maximumBasePrice);
            stat.avg.push(item.averageBasePrice);
          });
          setDates(dates);
          setStats(stat);
        } else {
          setDates([]);
          setStats({ min: [], max: [], avg: [] });
        }
      });
  }, [
    selectionRange,
    selectedCountry,
    selectedTransport,
    selectedOfferType,
    selectedDepartureCity,
    selectedPeriodLength,
    selectedMealCode,
  ]);

  return (
    <>
      {hasPermission("dashboard_analytics_destination") && (
        <>
          <Helmet>
            <title>
              Dashboard: {t("title.Destination by country")} | Qtravel Search
            </title>
          </Helmet>
          <Box
            sx={{
              backgroundColor: "background.default",
              minHeight: "100%",
              py: 4,
            }}
          >
            <Container>
              <Grid
                container
                alignItems="end"
                justifyContent="space-between"
                spacing={3}
              >
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {t("title.Destination by country")}
                  </Typography>
                  <Breadcrumbs
                    aria-label="breadcrumb"
                    separator={<ChevronRightIcon fontSize="small" />}
                    sx={{ mt: 1 }}
                  >
                    <Link
                      color="textPrimary"
                      component={RouterLink}
                      to="/dashboard/main"
                      variant="subtitle2"
                    >
                      Dashboard
                    </Link>
                    <Typography color="textSecondary" variant="subtitle2">
                      {t("title.Destination by country")}
                    </Typography>
                  </Breadcrumbs>
                </Grid>
                <Grid item>
                  <Button
                    aria-describedby={id}
                    variant="contained"
                    onClick={handleClick}
                    endIcon={<ChevronDownIcon fontSize="small" />}
                  >
                    {t("pages.prediction.analyze_range")}
                    {differenceInDays(
                      selectionRange[0].endDate,
                      selectionRange[0].startDate
                    ) + 1}{" "}
                    {t(`main.days`)}{" "}
                    {selectionRange && selectionRange[0] && (
                      <>
                        ({format(selectionRange[0].startDate, "dd-MM-yyyy")} -{" "}
                        {format(selectionRange[0].endDate, "dd-MM-yyyy")})
                      </>
                    )}
                  </Button>
                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                  >
                    <DateRangePicker
                      locale={i18n.language != "pl" ? undefined : pl}
                      staticRanges={[
                        {
                          label: t("other.today"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: date,
                            endDate: date,
                          }),
                          isSelected() {
                            return false;
                          },
                        },
                        {
                          label: t("other.last_week"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: subDays(
                              date,
                              ((date.getDay() + 7) % 7) + 6
                            ),
                            endDate: subDays(date, (date.getDay() + 7) % 7),
                          }),
                          isSelected() {
                            return false;
                          },
                        },
                        {
                          label: t("other.this_month"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: new Date(
                              date.getFullYear(),
                              date.getMonth(),
                              1
                            ),
                            endDate: new Date(
                              date.getFullYear(),
                              date.getMonth() + 1,
                              0
                            ),
                          }),
                          isSelected: () => false,
                        },
                        {
                          label: t("other.last_month"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: new Date(
                              date.getFullYear(),
                              date.getMonth() - 1,
                              1
                            ),
                            endDate: new Date(
                              date.getFullYear(),
                              date.getMonth(),
                              0
                            ),
                          }),
                          isSelected() {
                            return false;
                          },
                        },
                      ]}
                      months={2}
                      direction="horizontal"
                      ranges={selectionRange}
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      onChange={(item) => setState([item.selection])}
                    />
                  </Popover>
                </Grid>
              </Grid>
              <Typography color="textSecondary" variant="h6" sx={{ py: 3 }}>
                {t("pages.prediction.select_msg")}
              </Typography>
              <Autocomplete
                disablePortal
                options={countries}
                defaultValue={{ value: "GR", label: "Greece" }}
                sx={{
                  mr: 3,
                  width: "450px",
                  backgroundColor: "#fff",
                  borderRadius: "20px",
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t(`main.country`)} />
                )}
                onChange={(ev, value: any) => {
                  setSelectedCountry(value);
                }}
              />
              <Box sx={{ backgroundColor: "background.default", pt: 3 }}>
                <PredictedTrendsWithSelects
                  mealsOptions={mealTypes}
                  offerOptions={offerTypes}
                  transportOptions={transportTypes}
                  transportChanged={(value) => setSelectedTransport(value)}
                  offerTypeChanged={(value) => setSelectedOfferType(value)}
                  departureCityChanged={(value) =>
                    setSelectedDepartureCity(value)
                  }
                  periodLengthChanged={(value) =>
                    setSelectedPeriodLength(value)
                  }
                  mealCodeChanged={(value) => setSelectedMealCode(value)}
                  categories={dates}
                  data={stats}
                  title={t(`main.country`)}
                  tooltipInfo={t(`other.tooltip_Destination_country_1`)}
                />
              </Box>
              <Box sx={{ pt: 3 }}>
                <PredictedTrendsWithCheckboxes
                  categories1={cities}
                  categories2={[
                    "Pomerania",
                    "Crete",
                    "Belek",
                    "Corfu",
                    "Zakynthos",
                    "Canary Islands",
                    "Majorca",
                    "Sicily",
                  ]}
                  dataset1={{
                    min: [800, 1500, 2200, 3000, 7000, 10000, 6000, 4000.6],
                    max: [1800, 1700, 2600, 3500, 9500, 14000, 8000, 6000],
                    avg: [1300, 1600, 2400, 3300, 8800, 12000, 7000, 5000],
                  }}
                  dataset2={{
                    min: [
                      1700.4, 2700, 3400.5, 3400.5, 4400.5, 4800.8, 5100.8,
                      4000.6,
                    ],
                    max: [
                      2300.1, 3100, 3800.1, 3900, 4500.1, 5800.9, 5800.5,
                      6600.5,
                    ],
                    avg: [2000, 2900, 3700, 3600, 4400, 5200, 5500, 5800],
                  }}
                  label1={t(`main.cities`)}
                  label2={t(`main.regions`)}
                />
              </Box>
              <Box sx={{ pt: 3 }}>
                <PredictedTrendsWithCheckboxes
                  categories1={departureAirportCodeNames}
                  categories2={departureCityNames}
                  dataset1={departureStats}
                  dataset2={departureStats}
                  label1={t(`other.departure_airports`)}
                  label2={t(`main.departurecity`)}
                />
              </Box>
              <Grid container spacing={2} py={3}>
                <Grid item md={6} sm={6} xs={12}>
                  <PredictedTrends
                    categories={mealNames}
                    data={mealStats}
                    title={t(`other.board_type`)}
                    tooltipInfo={t(`other.tooltip_Destination_country_2`)}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <PredictedTrends
                    categories={propertyCategoryNames}
                    data={propertyCategoryStats}
                    title={t(`other.property_category`)}
                    tooltipInfo={t(`other.property_category`)}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <PredictedTrends
                    categories={transportTypeNames}
                    data={transportTypeStats}
                    title={t(`main.transporttype`)}
                    tooltipInfo={t(`other.tooltip_Destination_country_3`)}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <PredictedTrends
                    categories={tripDurationNames}
                    data={tripDurationStats}
                    title={t(`other.trip_durations`)}
                    tooltipInfo={t(`other.tooltip_Destination_country_4`)}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
        </>
      )}
    </>
  );
};

export default DestinationByCountry;
